import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Card,
} from '@mui/material';
import ModernButton from '../common/ModernButton';

/**
 * QuestionDetailsDialog - Dialog to view question details
 *
 * @param {Object} props
 * @param {Object} props.question - Question object to display
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {string} props.questionType - Type of question set
 * @param {string} props.sectionValue - Section value (passage/part)
 */
const QuestionDetailsDialog = ({
  question,
  open,
  onClose,
  questionType,
  sectionValue,
}) => {
  if (!question) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Question Details</DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ mt: 2 }}>
          {/* Question Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                backgroundColor: 'primary.main',
                width: 48,
                height: 48,
                mr: 2,
              }}
            >
              {question.questionNumber}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Question {question.questionNumber}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Chip
                  label={questionType || 'N/A'}
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
                <Chip
                  label={sectionValue}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          {/* Question Information */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}
                >
                  Question Text
                </Typography>
                <Typography variant="body2">
                  {question.question || question.questionText || 'No question text available'}
                </Typography>
              </Card>
            </Grid>

            {/* Options if available */}
            {question.options && question.options.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}
                  >
                    Options
                  </Typography>
                  {question.options.map((option, idx) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                      {String.fromCharCode(65 + idx)}. {option}
                    </Typography>
                  ))}
                </Card>
              </Grid>
            )}

            {/* Correct Answer if available */}
            {question.correctAnswer && (
              <Grid item xs={12}>
                <Card sx={{ p: 2, backgroundColor: 'success.50', border: 1, borderColor: 'success.main' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}
                  >
                    Correct Answer
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'success.dark' }}>
                    {question.correctAnswer}
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <ModernButton onClick={onClose} variant="outlined">
          Close
        </ModernButton>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionDetailsDialog;
